import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useConciergeChooser } from '@/features/care-concierge/chooser/ConciergeChooserProvider';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  BestMatchHighlight,
  findBestMatchEntry,
  KeyResourcesPanel,
  KeyResourcesPanelSkeleton,
  NavigatorResults,
  navigatorResultsCopy,
} from '@/shared/components/navigator-results';
import { useSharedCarePlan, useSharedCareGuideHubResources } from '../hooks/useSharedCarePlan';
import { buildSharedCarePlanViewModel } from '../utils/sharedCarePlanAdapter';
import { CareSeekerConciergeBanner } from './CareSeekerConciergeBanner';

/**
 * Read-only Care Seeker view of a care plan an FP shared with them. Reuses the
 * shared NavigatorResults presentation; the engagement is fetched live (latest
 * version) with provider cards already hydrated by the backend. Selection is
 * read-only, but empty specialties still offer the Care Concierge CTA (parity
 * with FP ResultsPage / CareSeekerResults).
 */
export const SharedCarePlanView = () => {
  const { engagementId } = useParams<{ engagementId: string }>();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const { openConcierge } = useConciergeChooser();

  const { data: detail, isLoading, isError } = useSharedCarePlan(engagementId);
  // Feature 078: Key Resources — same bundle the FP/PDF use, behind the shared-plan auth.
  const { data: resourceBundle, isLoading: resourceBundleLoading } =
    useSharedCareGuideHubResources(engagementId);

  const viewModel = useMemo(
    () => (detail ? buildSharedCarePlanViewModel(detail) : null),
    [detail],
  );

  // Feature 087 (ticket 16) — frozen Best Match, exposed as-is (no recompute).
  const liveBestMatch = viewModel ? findBestMatchEntry(viewModel) : undefined;

  const backLink = (
    <button
      type="button"
      onClick={() => navigate('/care-navigator/dashboard')}
      className="inline-flex items-center gap-1 text-sm text-sage hover:text-[hsl(169_95%_18%)]"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      Back to your care guides
    </button>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-10">
        {isLoading ? (
          <div className="space-y-6">
            {backLink}
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-48 w-full rounded-2xl" />
          </div>
        ) : isError || !viewModel ? (
          <div className="space-y-6">
            {backLink}
            <div className="rounded-2xl border border-dashed border-border-subtle bg-card-subtle p-10 text-center">
              <p className="font-termina text-lg font-medium text-text-primary">
                This care guide isn&apos;t available
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                It may have been removed, or it was not shared with your account.
              </p>
              <Button className="mt-5" onClick={() => navigate('/care-navigator/dashboard')}>
                Go to your care guides
              </Button>
            </div>
          </div>
        ) : (
          <NavigatorResults
            viewModel={viewModel}
            copy={navigatorResultsCopy['care-seeker']}
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
            topSlot={backLink}
            beforeTabsSlot={liveBestMatch ? <BestMatchHighlight entry={liveBestMatch} /> : undefined}
            footerSlot={
              <CareSeekerConciergeBanner onContactClick={() => openConcierge('navigator-banner')} />
            }
            onConciergeRequest={() => openConcierge('navigator-banner')}
            renderKeyResources={(category) =>
              resourceBundleLoading ? (
                <KeyResourcesPanelSkeleton />
              ) : (
                <KeyResourcesPanel
                  specialtyResources={
                    resourceBundle?.specialtyResourcesBySpecialtyId[category.id] ?? []
                  }
                  careStageResources={resourceBundle?.careStageResources ?? []}
                />
              )
            }
          />
        )}
      </div>
    </div>
  );
};
